import getAssetByID from '@cartwhl/common/api/getAssetByID';
import getMotionsByGroup, {
  type MotionRecord,
} from '@cartwhl/common/api/getMotionsByGroup';

import { isComicMogenGroupID } from '../../../AssetLibrary/assetLibraryGroupIds';

export type ComicMogenJobActorsPreview = {
  bodyCount: number;
  bvhURLs: string[];
  mhrBvhURLs: string[];
  mhrIdentityJsonURLs: string[];
};

const EMPTY_PREVIEW: ComicMogenJobActorsPreview = {
  bodyCount: 1,
  bvhURLs: [],
  mhrBvhURLs: [],
  mhrIdentityJsonURLs: [],
};

function trimUrls(urls: (string | null | undefined)[] | undefined): string[] {
  return (urls ?? []).map((url) => url?.trim() ?? '').filter(Boolean);
}

function previewFromMotion(motion: MotionRecord): ComicMogenJobActorsPreview {
  const bvhURLs = trimUrls(motion.bvhURLs ?? motion.bvh_urls);
  const mhrBvhURLs = trimUrls(motion.mhrBvhURLs);
  const mhrIdentityJsonURLs = trimUrls(motion.mhrIdentityJsonURLs);
  const exportCount = (motion.exportURLs ?? motion.export_urls)?.length ?? 0;

  return {
    bodyCount: Math.max(bvhURLs.length, mhrBvhURLs.length, exportCount, 1),
    bvhURLs,
    mhrBvhURLs,
    mhrIdentityJsonURLs,
  };
}

/**
 * Resolves body count and studio BVH URLs for a comic mogen job so Cartographer
 * can prompt for an actor and preview the multi-person stage.
 * Non-comic / unresolved jobs return `bodyCount: 1` with empty URLs.
 */
export async function resolveComicMogenJobActors(
  apiKey: string,
  jobId: string,
): Promise<ComicMogenJobActorsPreview> {
  const { data, error } = await getAssetByID(apiKey, jobId);
  if (error || !data?.assets?.length) {
    return EMPTY_PREVIEW;
  }

  const jobAsset =
    data.assets.find((asset) => asset.assetID === jobId) ?? data.assets[0];
  const groupID = jobAsset?.groupID;
  if (!groupID || !isComicMogenGroupID(groupID)) {
    return EMPTY_PREVIEW;
  }

  let nextToken: string | undefined;
  do {
    const page = await getMotionsByGroup(groupID, apiKey, nextToken);
    if (page.error || !page.data) {
      return EMPTY_PREVIEW;
    }

    const motion = page.data.items.find((item) => item.jobID === jobId);
    if (motion) {
      return previewFromMotion(motion);
    }

    nextToken = page.data.nextToken;
  } while (nextToken);

  return EMPTY_PREVIEW;
}
